import React, { useEffect, useCallback, useRef, useState } from 'react';
import { SkeletonLoading } from '@saleshandy/design-system';
import { AlertTriangle, EmailBlock, Inbox, Mail } from '@saleshandy/icons';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Table from '../../../../../../../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../../../../../../../shared/design-system/components/organisms/table/types';
import { SeedAccountImages } from '../../../../../../../test-create-update/components/seed-esp-account/type';
import { Other } from '../../../../../../../../../../shared/svg/other';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../../../shared/design-system/components/overlay';
import { getTooltipText } from '../../../../../../../../../sequence/components/sequence-single-content/components/sequence-single-header-actions/utils/helper';
import {
  SeedTestPauseReason,
  SeedTestSenderEmailAccountPauseReasonMessages,
} from '../../../../../../../../enums/inbox-radar';
import { accessibleOnClick } from '../../../../../../../../../../shared/utils/accessible-on-click';
import { useFirebaseAnimation } from '../../../utils/use-firebase-animation';

const Overview: React.FC<{
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  testReportOverviewData: any;
  onSpamScoreClick?: (row: any) => void;
  hasMoreSenderEmailAccounts: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  firebaseData?: any;
  sentCount?: number;
}> = ({
  scrollContainerRef,
  testReportOverviewData,
  onSpamScoreClick,
  hasMoreSenderEmailAccounts,
  onLoadMore,
  isLoadingMore,
  firebaseData,
  sentCount,
}) => {
  const { t } = useTranslation();
  const scrollHandlerRef = useRef<() => void>();
  const [hoveredSpamScoreRowId, setHoveredSpamScoreRowId] = useState<
    string | null
  >(null);

  // Use Firebase animation hook
  const { shouldAnimateItem } = useFirebaseAnimation(firebaseData, {
    duration: 2000,
    fields: [
      'inboxCount',
      'spamCount',
      'otherCount',
      'notDeliveredCount',
      'spamScore',
    ],
  });

  const isFireBaseConnected = useSelector(
    (state: any) => state?.inboxRadar?.isFireBaseConnected,
  );

  const handleScroll = useCallback(() => {
    if (scrollContainerRef?.current) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
      } = scrollContainerRef.current;
      // Load more when the user is 200px from the bottom
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        if (hasMoreSenderEmailAccounts && !isLoadingMore) {
          onLoadMore();
        }
      }
    }
    return undefined;
  }, [hasMoreSenderEmailAccounts, isLoadingMore, onLoadMore]);

  scrollHandlerRef.current = handleScroll;

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (container) {
      const scrollHandler = () => scrollHandlerRef.current?.();
      container.addEventListener('scroll', scrollHandler);
      return () => container.removeEventListener('scroll', scrollHandler);
    }
    return undefined;
  }, [scrollContainerRef]);

  const getEmailTypeIcon = (emailType: string) => {
    if (SeedAccountImages[emailType]) {
      return (
        <img
          src={SeedAccountImages[emailType]}
          alt={emailType}
          width={16}
          height={16}
        />
      );
    }
    return <Other height={16} width={16} />;
  };

  const getSpamAssassinScoreTag = (score: number) => {
    if (score) {
      if (score < 0) {
        return {
          label: 'Extremely Safe',
          key: 'extremely-safe',
          tooltip: 'Your email has no spam signals. Excellent for outreach',
        };
      }
      if (score >= 0 && score <= 2) {
        return {
          label: 'Safe',
          key: 'safe',
          tooltip:
            'Your email is safe to send. Very low chance of being flagged',
        };
      }
      if (score > 2 && score <= 5) {
        return {
          label: 'Warning',
          key: 'warning',
          tooltip:
            'Your email might be flagged. Make improvements before sending.',
        };
      }
      if (score > 5) {
        return {
          label: 'Spam Risk',
          key: 'spam-risk',
          tooltip:
            'High chance of landing in spam. Review and fix before sending.',
        };
      }
    }
    return { label: '', key: '' };
  };

  const getFirebaseDataForId = (seedTestSenderEmailAccountId: number) => {
    // Get all available IDs from Firebase data
    const firebaseIds = firebaseData ? Object.keys(firebaseData) : [];
    // Find matching Firebase data for current item's ID
    const firebaseItemData = firebaseIds.includes(
      seedTestSenderEmailAccountId?.toString(),
    )
      ? firebaseData[seedTestSenderEmailAccountId]
      : null;
    return firebaseItemData;
  };

  const getFirebasePercentage = (
    seedTestSenderEmailAccountId: number,
    countType: string,
  ) => {
    const fbData = getFirebaseDataForId(seedTestSenderEmailAccountId);

    if (fbData && fbData.placementSummary && fbData.totalCount) {
      const count = fbData.placementSummary?.[countType]?.total;
      // Return null if count is null or undefined
      if (count === null || count === undefined) {
        return null;
      }
      const percentage = Math.round((count / fbData.placementCount) * 100);
      return percentage;
    }
    return null;
  };

  // Helper function to check if any count has a value for the given account
  const hasAnyCountValue = (seedTestSenderEmailAccountId: number) => {
    const countTypes = [
      'inboxCount',
      'spamCount',
      'otherCount',
      'notDeliveredCount',
    ];
    return countTypes.some((countType) => {
      const fbData = getFirebaseDataForId(seedTestSenderEmailAccountId);
      if (fbData && fbData.placementSummary) {
        const count = fbData.placementSummary?.[countType]?.total;
        return count !== null && count !== undefined && count > 0;
      }
      return false;
    });
  };

  // Helper function to get percentage display value
  const getPercentageDisplay = (
    seedTestSenderEmailAccountId: number,
    countType: string,
  ) => {
    // Check if we should show dashes (sentCount is 0 and Firebase is not connected)
    const shouldShowAsPerSentCount = sentCount === 0 && !firebaseData;

    if (shouldShowAsPerSentCount) {
      return '-';
    }

    if (!isFireBaseConnected) {
      return false;
    }

    const fbPercentage = getFirebasePercentage(
      seedTestSenderEmailAccountId,
      countType,
    );

    // If Firebase percentage is valid, show it
    if (fbPercentage !== undefined && fbPercentage !== null) {
      return `${fbPercentage}%`;
    }

    // Check if any count has a value for this account
    const hasAnyValue = hasAnyCountValue(seedTestSenderEmailAccountId);

    // If any count has a value but current count is null, show 0%
    if (hasAnyValue) {
      return '0%';
    }

    // If all counts are null, show '-'
    return '-';
  };

  // Helper function to get pause reason identifier
  const getPauseReasonIdentifier = (row: any) => {
    const fbData = getFirebaseDataForId(row?.seedTestSenderEmailAccountId);
    return (
      (fbData?.pausedReasonIdentifier !== null &&
        fbData?.pausedReasonIdentifier) ||
      row.pausedReasonIdentifier
    );
  };

  // Helper function to check if should show pause warning
  const shouldShowPauseWarning = (row: any) => {
    const pauseReason = getPauseReasonIdentifier(row);
    return (
      pauseReason && pauseReason !== SeedTestPauseReason.ManuallyPausedByUser
    );
  };

  // Helper function to get spam score
  const getSpamScore = (row: any) => {
    const fbData = getFirebaseDataForId(row?.seedTestSenderEmailAccountId);
    return fbData?.spamAnalysis?.score ?? row?.spamAssassinScore?.score;
  };

  const handleSpamScoreClick = (row: any) => {
    setHoveredSpamScoreRowId(null);
    onSpamScoreClick?.(row);
  };

  return (
    <div className="overview-container">
      <div className="overview-table-container w-100">
        <Table
          columns={[
            {
              dataField: 'fromEmail',
              text: 'Sender Email Accounts',
              headerAlign: 'start',
              component: (cell, row) => {
                const email = row?.fromEmail;
                const pauseReason = getPauseReasonIdentifier(row);

                return (
                  <div className="d-flex align-items-center gap-8">
                    {getEmailTypeIcon(row?.emailType)}
                    <span className="email-address">{email}</span>
                    {shouldShowPauseWarning(row) && (
                      <OverlayTooltip
                        text={getTooltipText(
                          SeedTestSenderEmailAccountPauseReasonMessages[
                            pauseReason
                          ],
                          t,
                        )}
                        placement={Placement.Auto}
                      >
                        <span className="sender-email-account-paused-reason">
                          <AlertTriangle color="#B91C1C" />
                        </span>
                      </OverlayTooltip>
                    )}
                  </div>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'inbox',
              text: 'Inbox',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <Inbox width={16} height={16} color="#059669" />
                  <span>Inbox</span>
                </div>
              ),
              component: (cell, row) => {
                const accountId = row?.seedTestSenderEmailAccountId;
                const shouldAnimate = shouldAnimateItem(
                  `${accountId}-inboxCount`,
                );
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {getPercentageDisplay(
                      row?.seedTestSenderEmailAccountId,
                      'inboxCount',
                    ) || `${row?.inbox?.percentage}%`}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'spam',
              text: 'Spam',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <AlertTriangle width={16} height={16} color="#DC2626" />
                  <span>Spam</span>
                </div>
              ),
              component: (cell, row) => {
                const accountId = row?.seedTestSenderEmailAccountId;
                const shouldAnimate = shouldAnimateItem(
                  `${accountId}-spamCount`,
                );
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {getPercentageDisplay(
                      row?.seedTestSenderEmailAccountId,
                      'spamCount',
                    ) || `${row?.spam?.percentage}%`}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'other',
              text: 'Other',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <Mail width={16} height={16} color="#D97706" />
                  <span>Other</span>
                </div>
              ),
              component: (cell, row) => {
                const accountId = row?.seedTestSenderEmailAccountId;
                const shouldAnimate = shouldAnimateItem(
                  `${accountId}-otherCount`,
                );
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {getPercentageDisplay(
                      row?.seedTestSenderEmailAccountId,
                      'otherCount',
                    ) || `${row?.other?.percentage}%`}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'notDelivered',
              text: 'Undelivered',
              headerAlign: 'start',
              headerComponent: () => (
                <div className="d-flex align-items-center gap-4">
                  <EmailBlock width={16} height={16} color="#1f2937" />
                  <span>Undelivered</span>
                </div>
              ),
              component: (cell, row) => {
                const accountId = row?.seedTestSenderEmailAccountId;
                const shouldAnimate = shouldAnimateItem(
                  `${accountId}-notDeliveredCount`,
                );
                return (
                  <span
                    className={shouldAnimate ? 'esp-count-pop-animation' : ''}
                  >
                    {getPercentageDisplay(
                      row?.seedTestSenderEmailAccountId,
                      'notDeliveredCount',
                    ) || `${row?.notDelivered?.percentage}%`}
                  </span>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'spamAssassinScore',
              text: 'SpamAssassin Score',
              headerAlign: 'start',
              component: (cell, row) => {
                const score = getSpamScore(row);
                const accountId = row?.seedTestSenderEmailAccountId;
                const shouldAnimate = shouldAnimateItem(
                  `${accountId}-spamScore`,
                );

                if (score === null || score === undefined) {
                  return <span>-</span>;
                }
                const rowId = `${row?.fromEmail}-${row?.spamAssassinScore?.score}`;
                return (
                  <div>
                    <OverlayTooltip
                      text="View Spamassassin Report"
                      placement={Placement.Bottom}
                      show={hoveredSpamScoreRowId === rowId}
                    >
                      <span
                        className={`score-cell cursor-pointer ${
                          getSpamAssassinScoreTag(score)?.key
                        } ${shouldAnimate ? 'esp-count-pop-animation' : ''}`}
                        onMouseEnter={() => setHoveredSpamScoreRowId(rowId)}
                        onMouseLeave={() => setHoveredSpamScoreRowId(null)}
                        {...accessibleOnClick(() => handleSpamScoreClick(row))}
                      >
                        {score}
                      </span>
                    </OverlayTooltip>
                  </div>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
            {
              dataField: 'riskLevel',
              text: 'Risk Level',
              headerAlign: 'start',
              component: (cell, row) => {
                const score = getSpamScore(row);

                return (
                  <div>
                    {score !== null && score !== undefined ? (
                      <OverlayTooltip
                        text={getSpamAssassinScoreTag(score)?.tooltip}
                        placement={Placement.Auto}
                      >
                        <span
                          className={`risk-level-tag cursor-pointer ${
                            getSpamAssassinScoreTag(score)?.key
                          }`}
                        >
                          {getSpamAssassinScoreTag(score)?.label}
                        </span>
                      </OverlayTooltip>
                    ) : (
                      '-'
                    )}
                  </div>
                );
              },
              headerLoadingSkeleton: (
                <SkeletonLoading width={100} height={20} />
              ),
              cellLoadingSkeleton: <SkeletonLoading width={100} height={20} />,
            },
          ]}
          data={testReportOverviewData || []}
          pagination={PaginationShowHide.HIDE}
          paginationOptions={{
            options: {
              limit: testReportOverviewData?.length || 0,
              page: 1,
              totalElements: testReportOverviewData?.length || 0,
            },
          }}
        />
        {isLoadingMore && (
          <div className="d-flex justify-content-center align-items-center m-4">
            <Spinner className="spinner--blue" animation="border" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
